import { useCallback, useEffect, useSyncExternalStore } from "react";

function dispatchStorageEvent(key: string, newValue: string | null) {
	window.dispatchEvent(new StorageEvent("storage", { key, newValue }));
}

const setLocalStorageItem = <T>(key: string, value: T): void => {
	const stringifiedValue = JSON.stringify(value);
	window.localStorage.setItem(key, stringifiedValue);
	dispatchStorageEvent(key, stringifiedValue);
};

const removeLocalStorageItem = (key: string): void => {
	window.localStorage.removeItem(key);
	dispatchStorageEvent(key, null);
};

const getLocalStorageItem = (key: string): string | null => {
	return window.localStorage.getItem(key);
};

const useLocalStorageSubscribe = (callback: (event: StorageEvent) => void) => {
	window.addEventListener("storage", callback);
	return () => window.removeEventListener("storage", callback);
};

const getLocalStorageServerSnapshot = (): never => {
	throw new Error("useLocalStorage is a client-only hook");
};

function useLocalStorage<T>(
	key: string,
	initialValue?: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
	const getSnapshot = () =>
		getLocalStorageItem(key) ?? JSON.stringify(initialValue);

	const store = useSyncExternalStore(
		useLocalStorageSubscribe,
		getSnapshot,
		getLocalStorageServerSnapshot,
	);

	const setState = useCallback(
		(value: React.SetStateAction<T>) => {
			try {
				const parsedStore = store ? (JSON.parse(store) as T) : undefined;
				const nextState =
					typeof value === "function"
						? (value as (prev: T | undefined) => T)(parsedStore)
						: value;

				if (nextState === undefined || nextState === null) {
					removeLocalStorageItem(key);
				} else {
					setLocalStorageItem(key, nextState);
				}
			} catch (e) {
				console.warn(e);
			}
		},
		[key, store],
	);

	useEffect(() => {
		if (getLocalStorageItem(key) === null && initialValue !== undefined) {
			setLocalStorageItem(key, initialValue);
		}
	}, [key, initialValue]);

	return [store ? (JSON.parse(store) as T) : (initialValue as T), setState];
}

export default useLocalStorage;
